import {Component, Host, Prop, h, forceUpdate} from "@stencil/core";

import {i18nProvider} from "@trumpf/ux.i18n";
import {settingsTranslations as translations} from "../../translations";

import {Topic, EditFooter} from "./notification-settings.sub";
import {CategoriesFC} from "./Categories.sub";
import {AssetsFC} from "./Assets.sub";

import {userSettingsStore as store} from "../stores/userSettingsStore";
import type {Stage} from "../stores/apiService";

const {i18n} = i18nProvider;

/**
 * Notification settings component to manage user notification settings.
 */
@Component({
	tag: "uxn-notification-settings",
	styleUrl: "notification-settings.scss",
	shadow: false,
})
export class NotificationSettings {
	/** locale */
	@Prop() locale: string = "de_DE";
	/** stage */
	@Prop() stage: Stage = "PROD";
	/** toggle debugging */
	@Prop() debug: boolean = false;

	#i18nSubscriberId: string;

	async componentWillLoad() {
		this.#customTranslation();
		await i18nProvider.changeLanguage(this.locale);
		this.#i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this)); //TODO check if this is needed
	}

	async componentDidLoad() {
		await store.init({
			locale: this.locale,
			stage: this.stage.toUpperCase() as Stage,
			debug: this.debug,
		});
	}

	disconnectedCallback() {
		i18nProvider.unsubscribe(this.#i18nSubscriberId);
		store.reset();
	}

	#customTranslation() {
		Object.entries(translations).forEach(([language, bundle]) => i18n.addResourceBundle(language, "settings", bundle));

		i18n.on("languageChanged", (lng) => {
			if (Object.keys(translations).includes(lng)) return;

			const lngCode = lng.split("_")[0];
			const fallbackLng = Object.keys(translations).find((key) => key.startsWith(lngCode));
			if (fallbackLng) i18n.changeLanguage(fallbackLng); //TODO fix async
		});
	}

	render() {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		return (
			<Host class="uxn-notification-settings gr-8 push-2 no-gutter gr-10@t push-1@t gr-10@m push-1@m no-gutter@m">
				<Topic headline="settings:page.headline" text="settings:page.text" />
				<CategoriesFC
					topic={{headline: "settings:categories.headline", text: "settings:categories.text"}}
					table={{
						categoryHeader: "settings:categories.table.categories.header",
						notificationChannel: {caption: "settings:categories.table.notifications.header", tooltip: "settings:categories.table.notifications.tooltip"},
						eMailChannel: {caption: "settings:categories.table.emails.header", tooltip: "settings:categories.table.emails.tooltip"},
					}}
					categories={store.categories}
				>
					<EditFooter
						hasChanges={store.categories.hasChanges}
						hasFailedLoading={store.categories.hasFailedGet || store.categories.hasFailedPost}
						updateCallback={store.updateCategories}
						resetCallback={store.resetCategories}
					/>
				</CategoriesFC>
				<AssetsFC
					topic={{headline: "settings:assets.headline", text: "settings:assets.text"}}
					table={{header: "settings:assets.table.myAssets"}}
					assets={store.assets}
				>
					<EditFooter
						hasChanges={store.assets.hasChanges}
						hasFailedLoading={store.assets.hasFailedGet || store.assets.hasFailedPost}
						updateCallback={store.updateAssets}
						resetCallback={store.resetAssets}
					/>
				</AssetsFC>
			</Host>
		);
	}
}
